import { useCallback } from 'react';
import { useRouter } from 'next/router';
import cl from 'classnames';

import { ClassName } from '../../../../../../types';
import { IconsEnum } from '../../../../../../assets/icons/types';
import { PrimaryNavTabsEnum } from '../../../../../hooks/useMenu/types';
import { TooltipDelay } from '../../../../../../helpers/tooltips/types';
import { PrimaryNavTooltipMenuSection } from './PrimaryNavTooltipMenuWithSectionsTab.types';
import { TeamNanoID } from '../../../../../../main/teams/teamsTypes';

import { useMenu } from '../../../../../hooks/useMenu';

import { PrimaryNavTooltipMenuWithSectionsTabSection } from './components/PrimaryNavTooltipMenuWithSectionsTabSection';

import { ContentWithTooltip } from '../../../../../../helpers/ContentWithTooltip';
import { LocalForage } from '../../../../../../utils/LocalForage';
import { PureIconButtonHelper } from '../../../../../../helpers/buttons/PureIconButtonHelper';

import { getNavMenuTabPath } from '../../../../../utils/getNavMenuTabPath';
import {
  bodyTooltipContainer,
  TooltipPlacement,
  zeroOffset
} from '../../../../../../helpers/tooltips/tooltipsConstants';

const tooltipDelay: TooltipDelay = [0, 0];

interface PrimaryNavTooltipMenuWithSectionsTabProps {
  addClassName?: ClassName;
  companyNanoId?: TeamNanoID;
  href: string;
  icon: IconsEnum;
  tab: PrimaryNavTabsEnum;
  sections: PrimaryNavTooltipMenuSection[];
  onlyHref?: boolean;
}

function PrimaryNavTooltipMenuWithSectionsTab({
  addClassName,
  companyNanoId,
  href,
  icon,
  tab,
  sections,
  onlyHref
}: PrimaryNavTooltipMenuWithSectionsTabProps) {
  const { push: redirect, asPath } = useRouter();

  const { currentPathTab } = useMenu();

  const handleClick = useCallback<() => void>(() => {
    if (onlyHref) {
      return redirect(href);
    }

    LocalForage.getItem<string>(getNavMenuTabPath(tab, companyNanoId)).then(
      (path) => {
        if (path) {
          return redirect(path);
        }

        return redirect(href);
      }
    );
  }, [companyNanoId, href, onlyHref, redirect, tab]);

  return (
    <ContentWithTooltip
      appendTo={bodyTooltipContainer}
      className="flex flex-shrink-0 items-center justify-center h-full sm:h-14 flex-grow sm:w-14 sm:flex-grow-0"
      delay={tooltipDelay}
      interactive
      offset={zeroOffset}
      tooltipClassName="mt-0.5"
      tooltipPlacement={TooltipPlacement.RIGHT_START}
      tooltipChildren={
        <div className="relative min-w-48 z-10 overflow-y-auto bg-white border border-transparent dark:bg-gray-700 dark:border-opacity-10 dark:border-white dark:text-white focus:outline-none -my-2 -mx-2.5 py-1 ring-1 ring-black ring-opacity-5 rounded-md shadow-lg text-gray-700">
          {sections.map(({ links, sectionI18nText }, index) => (
            <PrimaryNavTooltipMenuWithSectionsTabSection
              key={index}
              asPath={asPath}
              tab={tab}
              tooltipI18nText={sectionI18nText}
              companyNanoId={companyNanoId}
              tooltipLinks={links}
            />
          ))}
        </div>
      }
    >
      <PureIconButtonHelper
        className={cl(
          'inline-flex flex-shrink-0 items-center justify-center h-full sm:h-14 flex-grow sm:w-14 sm:rounded-lg sm:flex-grow-0 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 stroke-1.75',
          currentPathTab === tab
            ? 'text-blue-500 bg-blue-500 bg-opacity-20 dark:text-blue-400'
            : 'hover:bg-gray-200 dark:hover:bg-gray-700',
          addClassName
        )}
        iconClassName="h-6 w-6 sm:h-7 sm:w-7"
        icon={icon}
        onClick={handleClick}
      />
    </ContentWithTooltip>
  );
}

export default PrimaryNavTooltipMenuWithSectionsTab;
